import { cn } from "@/utils/cn";
import { ArrowUpRight } from "lucide-react";
import type { DiagramStage, Stage, WorkflowSummary } from "../types";

type Props = {
  stages: Stage[];
  workflows: WorkflowSummary[];
  selected: WorkflowSummary;
  onSelect: (name: string) => void;
  /** Opens the text of a step, when the steps are loaded. */
  onOpenStep?: (number: number) => void;
};

const LINK =
  "inline-flex items-center gap-1 rounded-full border border-blue-200 bg-blue-50 px-2 py-0.5 text-2xs font-medium text-blue-700 hover:border-blue-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-400";

const stepRange = (part: DiagramStage): string => {
  const numbers = part.steps.map((step) => step.number);
  const first = Math.min(...numbers);
  const last = Math.max(...numbers);
  return first === last ? `Step ${first}` : `Steps ${first}–${last}`;
};

const WorkflowLink = ({
  workflow,
  onSelect,
}: {
  workflow: WorkflowSummary;
  onSelect: (name: string) => void;
}) => (
  <button
    type="button"
    className={LINK}
    aria-label={`Open the ${workflow.label} workflow`}
    onClick={() => onSelect(workflow.name)}
  >
    {workflow.label}
    <ArrowUpRight className="h-3 w-3" aria-hidden />
  </button>
);

/** The stages of the selected workflow on the track of stages that all workflows share.
 * The track does not move when the user selects a different workflow, so the stages that two
 * workflows have in common stay in the same place. */
export const WorkflowDiagram = ({ stages, workflows, selected, onSelect, onOpenStep }: Props) => {
  const byName = new Map(workflows.map((workflow) => [workflow.name, workflow]));
  const parts = new Map(selected.diagram.map((part) => [part.stage, part]));
  const partOf = workflows.filter((workflow) =>
    workflow.diagram.some((part) => part.workflows.includes(selected.name)),
  );
  const sharedCount = (stage: string) =>
    workflows.filter(
      (workflow) =>
        workflow.name !== selected.name && workflow.diagram.some((part) => part.stage === stage),
    ).length;

  if (selected.diagram.length === 0) return null;

  return (
    <figure aria-labelledby="workflow-diagram-title" className="space-y-3">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h3 id="workflow-diagram-title" className="text-base font-semibold text-zinc-900">
          Diagram
        </h3>
        {partOf.length > 0 && (
          <p className="flex flex-wrap items-center gap-1.5 text-xs text-zinc-600">
            Part of
            {partOf.map((workflow) => (
              <WorkflowLink key={workflow.name} workflow={workflow} onSelect={onSelect} />
            ))}
          </p>
        )}
      </div>
      <figcaption className="max-w-3xl text-sm leading-6 text-zinc-600">
        All workflows use the same stages. The dark stages are in this workflow. Select a
        different workflow to see which stages it has in common with this one.
      </figcaption>
      <ol
        aria-label={`Stages of the ${selected.label} workflow`}
        className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4"
      >
        {stages.map((stage, index) => {
          const part = parts.get(stage.id);
          const shared = sharedCount(stage.id);
          return (
            <li key={stage.id} className="flex min-w-0 flex-col gap-2">
              <div className="flex items-center gap-2">
                <span
                  className={cn(
                    "flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-2xs font-semibold motion-safe:transition-colors motion-safe:duration-300",
                    part ? "bg-zinc-900 text-white" : "border border-dashed border-zinc-300 text-zinc-400",
                  )}
                  aria-hidden
                >
                  {index + 1}
                </span>
                <span
                  className={cn(
                    "text-xs font-semibold uppercase tracking-wide motion-safe:transition-colors motion-safe:duration-300",
                    part ? "text-zinc-900" : "text-zinc-400",
                  )}
                >
                  {stage.label}
                </span>
                <span className="h-px flex-1 bg-zinc-200" aria-hidden />
              </div>
              <div
                className={cn(
                  "flex flex-1 flex-col gap-2 rounded-lg border p-3 motion-safe:transition-colors motion-safe:duration-300",
                  part
                    ? "border-zinc-300 bg-white shadow-sm"
                    : "border-dashed border-zinc-200 bg-zinc-50",
                )}
              >
                {part ? (
                  <>
                    <p className="text-sm font-semibold leading-5 text-zinc-900">{part.label}</p>
                    <p className="font-mono text-2xs text-zinc-500">{stepRange(part)}</p>
                    <ul className="space-y-1">
                      {part.steps.map((step) => (
                        <li key={step.number} className="text-xs leading-5 text-zinc-700">
                          {onOpenStep ? (
                            <button
                              type="button"
                              onClick={() => onOpenStep(step.number)}
                              className="text-left hover:text-zinc-900 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-zinc-400"
                            >
                              {step.number}. {step.label}
                            </button>
                          ) : (
                            `${step.number}. ${step.label}`
                          )}
                        </li>
                      ))}
                    </ul>
                    {part.workflows.length > 0 && (
                      <div className="flex flex-wrap gap-1.5">
                        {part.workflows.map((name) => {
                          const linked = byName.get(name);
                          return linked ? (
                            <WorkflowLink key={name} workflow={linked} onSelect={onSelect} />
                          ) : null;
                        })}
                      </div>
                    )}
                    <p className="mt-auto text-2xs text-zinc-500">
                      {shared === 0
                        ? "Only in this workflow"
                        : `Also in ${shared} other workflow${shared === 1 ? "" : "s"}`}
                    </p>
                  </>
                ) : (
                  <p className="text-xs text-zinc-400">Not in this workflow</p>
                )}
              </div>
            </li>
          );
        })}
      </ol>
    </figure>
  );
};
